import React, { Component } from 'react';
import { Routes, Route } from 'react-router-dom';
import { withRouterLegacy } from 'core/routerUtils';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as trainingActions from 'features/training/duck';
import { getTrainingModules } from 'features/training/selectors';
import { TrainingDashboard, TrainingModule } from 'features/training/';
import { PageNotFound, Accordion, AlertNotice, Spinner } from 'shared/components';
import { sumBy, find, some } from 'lodash';

import './TrainingContainer.scss';

class TrainingContainer extends Component {
  constructor(props) {
    super(props);

    if (
      !props.modules.length &&
      (props.user.testCenterEventPersonnels.length || props.user.testCenterCyclePersonnels.length)
    ) {
      props.actions.getTrainingModules(props.user.id);
    }

    this.renderDashboard = this.renderDashboard.bind(this);
    this.onCompleteTask = this.onCompleteTask.bind(this);
  }

  onCompleteTask(taskId) {
    this.props.actions.completeTrainingTask(this.props.user.id, taskId);
  }

  renderDashboard() {
    const { location, modules } = this.props;
    const totalIncompleteModules = sumBy(modules, 'program.incompletedModules');
    return (
      <div className="training-info">
        <div className="header-section position-relative">
          <h4>{this.props.t('training.title')}</h4>

          {!!totalIncompleteModules && (
            <AlertNotice
              message={this.props.t('dashboard.incompleteProgramsModulesWarning', {
                count: totalIncompleteModules,
              })}
              type="warning"
            />
          )}

          {modules.length > 1 ? (
            <Accordion startPosition={location.state?.startPosition}>
              {modules.map((trainingProgram) => (
                <div
                  data-trigger={
                    trainingProgram.program.incompletedModules ? (
                      <React.Fragment>
                        {trainingProgram.program.name}
                        <span>
                          <i className="fa fa-exclamation-triangle pe-2" />
                          <span>
                            {this.props.t('dashboard.incompleteProgramModulesWarning', {
                              count: trainingProgram.program.incompletedModules,
                            })}
                          </span>
                        </span>
                      </React.Fragment>
                    ) : (
                      trainingProgram.program.name
                    )
                  }
                  key={`pragram-${trainingProgram.program.id}`}>
                  <TrainingDashboard
                    modules={trainingProgram.trainingModules}
                    key={trainingProgram.program.id}
                  />
                </div>
              ))}
            </Accordion>
          ) : (
            <TrainingDashboard modules={modules[0].trainingModules} />
          )}
        </div>
      </div>
    );
  }

  render() {
    const { modules, modulesLoading } = this.props;

    if (!modules.length && modulesLoading) {
      return <Spinner />;
    }

    return modules.length ? (
      <div className="mt-3">
        <Routes>
          <Route index element={<DashboardView parent={this} />} />
          <Route path=":moduleId" element={<ModuleView parent={this} />} />
          <Route path="*" element={<PageNotFound />} />
        </Routes>
      </div>
    ) : (
      <div className="mt-3">
        <h4 className="mb-3">{this.props.t('training.title')}</h4>
        <AlertNotice message={this.props.t('dashboard.noTrainingModulesMessage')} />
      </div>
    );
  }
}

// Helper components to access route params
const DashboardView = ({ parent }) => parent.renderDashboard();
DashboardView.propTypes = {
  parent: PropTypes.object.isRequired,
};

const ModuleView = withRouterLegacy(({ parent, match }) => {
  const moduleId = +match.params.moduleId;
  const program = find(parent.props.modules, (program) =>
    some(program.trainingModules, { id: moduleId })
  );
  const module = find(program.trainingModules, { id: moduleId });
  return (
    <TrainingModule
      module={module}
      program={program.program}
      user={parent.props.user}
      onComplete={parent.onCompleteTask}
    />
  );
});

ModuleView.propTypes = {
  parent: PropTypes.object.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      moduleId: PropTypes.string,
    }),
  }).isRequired,
};

function mapStateToProps(state) {
  return {
    user: state.users.currentUser,
    modules: getTrainingModules(state),
    modulesLoading: state.training.loading,
  };
}

function mapDispatchToProps(dispatch) {
  return { actions: bindActionCreators({ ...trainingActions }, dispatch) };
}

export default withTranslation()(
  withRouterLegacy(connect(mapStateToProps, mapDispatchToProps)(TrainingContainer))
);

TrainingContainer.propTypes = {
  // Props from react-router (via withRouterLegacy)
  location: PropTypes.shape({
    state: PropTypes.shape({
      startPosition: PropTypes.number,
    }),
  }),

  // Props from mapStateToProps
  modules: PropTypes.arrayOf(
    PropTypes.shape({
      program: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        incompletedModules: PropTypes.number,
      }),
      trainingModules: PropTypes.array,
    })
  ),
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    testCenterEventPersonnels: PropTypes.array,
    testCenterCyclePersonnels: PropTypes.array,
  }).isRequired,
  modulesLoading: PropTypes.bool.isRequired,

  // Props from mapDispatchToProps
  actions: PropTypes.shape({
    getTrainingModules: PropTypes.func.isRequired,
    completeTrainingTask: PropTypes.func.isRequired,
  }).isRequired,
  t: PropTypes.func.isRequired,
};

TrainingContainer.defaultProps = {
  modules: [],
};
